/**
 * StockBadge — kleines farbiges Pill das den WC-Lagerstatus zeigt.
 *
 * Quelle: WC stock_status ('instock' | 'outofstock' | 'onbackorder') plus
 * purchasable (false = nicht kaufbar, z.B. fehlender Preis bei variablen
 * Produkten ohne Default-Variation).
 *
 * Wenn `stockQuantity` gesetzt und niedrig (<10) → zusaetzlicher "wenig"-Hint.
 */
import { AlertTriangle, Check, Clock, XCircle } from "lucide-react";

type Props = {
  stockStatus: "instock" | "outofstock" | "onbackorder" | null;
  purchasable?: boolean;
  stockQuantity?: number | null;
  /** Kompaktes Badge (kleiner, ohne Icon). Default: false. */
  compact?: boolean;
};

export function StockBadge({
  stockStatus,
  purchasable = true,
  stockQuantity,
  compact = false,
}: Props) {
  // Nicht kaufbar = wichtigster Status (z.B. variable ohne preise)
  if (!purchasable) {
    return (
      <Pill color="rose" icon={<XCircle className="size-3" />} compact={compact}>
        Nicht kaufbar
      </Pill>
    );
  }

  if (stockStatus === "outofstock") {
    return (
      <Pill color="rose" icon={<XCircle className="size-3" />} compact={compact}>
        Ausverkauft
      </Pill>
    );
  }

  if (stockStatus === "onbackorder") {
    return (
      <Pill color="amber" icon={<Clock className="size-3" />} compact={compact}>
        Lieferbar (Nachbestellung)
      </Pill>
    );
  }

  // instock — schaue ob low-stock
  if (stockStatus === "instock") {
    const isLow =
      typeof stockQuantity === "number" && stockQuantity > 0 && stockQuantity < 10;
    if (isLow) {
      return (
        <Pill
          color="amber"
          icon={<AlertTriangle className="size-3" />}
          compact={compact}
        >
          Nur noch {stockQuantity} verfügbar
        </Pill>
      );
    }
    return (
      <Pill color="emerald" icon={<Check className="size-3" />} compact={compact}>
        Verfügbar
      </Pill>
    );
  }

  // Status null/unbekannt — kein Badge zeigen ist sauberer als "Status unbekannt"
  return null;
}

function Pill({
  color,
  icon,
  compact,
  children,
}: {
  color: "emerald" | "amber" | "rose";
  icon: React.ReactNode;
  compact: boolean;
  children: React.ReactNode;
}) {
  const palette = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  }[color];

  const size = compact
    ? "px-1.5 py-0.5 text-[10px] gap-0.5"
    : "px-2 py-0.5 text-[11px] gap-1";

  return (
    <span
      className={`inline-flex items-center rounded-md border font-medium ${palette} ${size}`}
    >
      {icon}
      {children}
    </span>
  );
}
